import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, HeartHandshake, Instagram, Facebook } from "lucide-react";
import { LOGO_URL, api, formatApiErrorDetail } from "../lib/api";

export const Footer = () => {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      const res = await api.post("/newsletter", { email });
      toast.success(res.data.message || "Merci pour votre inscription !");
      setEmail("");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <footer className="border-t border-white/10 bg-[#070707] mt-24" data-testid="main-footer">
      {/* Newsletter band */}
      <div className="border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-14 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-8">
          <div>
            <h3 className="font-display text-3xl text-white">Restez inspiré·e</h3>
            <p className="text-zinc-400 text-sm mt-2 max-w-md">
              Recevez les nouvelles œuvres et les histoires de nos artistes directement par email.
            </p>
          </div>
          <form onSubmit={subscribe} className="flex w-full lg:w-auto gap-3" data-testid="newsletter-form">
            <input
              data-testid="newsletter-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Votre adresse email"
              className="flex-1 lg:w-72 bg-[#0d0d0d] border border-white/15 px-4 py-3 text-white focus:border-[#2563EB] outline-none"
            />
            <button
              data-testid="newsletter-submit"
              type="submit"
              disabled={loading}
              className="inline-flex items-center gap-2 bg-[#2563EB] hover:bg-[#1D4ED8] disabled:opacity-60 text-white px-6 py-3 text-sm transition-colors whitespace-nowrap"
            >
              {loading && <Loader2 className="h-4 w-4 animate-spin" />} S'inscrire
            </button>
          </form>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16 grid md:grid-cols-4 gap-12">
        <div className="md:col-span-2">
          <div className="flex items-center gap-3 mb-5">
            <img src={LOGO_URL} alt="Logo" className="h-12 w-12 object-contain rounded-full" />
            <p className="font-display text-xl text-white">La Fondation</p>
          </div>
          <p className="text-zinc-400 text-sm leading-relaxed max-w-md mb-6">
            Une galerie dédiée aux artistes handicapés de France. Chaque œuvre achetée soutient
            directement son créateur.
          </p>
          <a
            href="https://www.leetchi.com/fr/c/la-fondation-des-artistes-handicapes-1454665"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="footer-leetchi"
            className="inline-flex items-center gap-2 bg-[#E11D48]/10 border border-[#E11D48]/40 text-[#E11D48] px-5 py-2.5 text-sm hover:bg-[#E11D48] hover:text-white transition-colors"
          >
            <HeartHandshake className="h-4 w-4" /> Soutenir la cagnotte
          </a>
        </div>
        <div>
          <h4 className="text-xs uppercase tracking-[0.25em] text-zinc-500 mb-5">Navigation</h4>
          <ul className="space-y-3 text-sm">
            <li><Link to="/galerie" className="text-zinc-300 hover:text-white transition-colors">Galerie</Link></li>
            <li><Link to="/artistes" className="text-zinc-300 hover:text-white transition-colors">Les Artistes</Link></li>
            <li><Link to="/artistes-celebres" className="text-zinc-300 hover:text-white transition-colors">Artistes Handicapés connus</Link></li>
            <li><Link to="/contact" className="text-zinc-300 hover:text-white transition-colors">Contact</Link></li>
            <li><Link to="/admin/login" className="text-zinc-500 hover:text-white transition-colors">Espace administrateur</Link></li>
          </ul>
        </div>
        <div>
          <h4 className="text-xs uppercase tracking-[0.25em] text-zinc-500 mb-5">Suivez-nous</h4>
          <p className="text-zinc-300 text-sm leading-relaxed mb-4">
            lafondationdesartisteshandicap@yahoo.com<br />Paris, France
          </p>
          <div className="flex gap-3">
            <a href="https://www.instagram.com/maxime_blot_lefevre_95/" target="_blank" rel="noopener noreferrer" aria-label="Instagram" data-testid="footer-instagram"
              className="h-9 w-9 flex items-center justify-center border border-white/15 text-zinc-300 hover:text-white hover:border-[#2563EB] transition-colors">
              <Instagram className="h-4 w-4" />
            </a>
            <a href="https://www.facebook.com/Blot1995/photos" target="_blank" rel="noopener noreferrer" aria-label="Facebook" data-testid="footer-facebook"
              className="h-9 w-9 flex items-center justify-center border border-white/15 text-zinc-300 hover:text-white hover:border-[#2563EB] transition-colors">
              <Facebook className="h-4 w-4" />
            </a>
          </div>
        </div>
      </div>
      <div className="border-t border-white/10 py-6 text-center text-xs text-zinc-600">
        © {new Date().getFullYear()} La Fondation des Artistes Handicapées de France. Tous droits réservés.
      </div>
    </footer>
  );
};
